import {
  canOwnIncidentWork,
  canOwnSafeguardingWork
} from '@features/organisation/utils/roleGroups'
import { SAFEGUARDING_ITEM_ID } from '@lmc/common/types'
import { Avatar, Stack, Text } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { type Home, IncidentSeverity } from '@lmc/ts-common'
import { IncidentReportSeverityEnum } from '@lmc/validations'
import {
  Form as AntdForm,
  Checkbox,
  DatePicker,
  Input,
  Select,
  TimePicker
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import useIncidentTypeOptions from 'hooks/useIncidentTypeOptions'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useGoals from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoals'
import styled from 'styled-components'
import { getSeverityOptions } from '../../../../utils/incidentSeverityUtils'
import IncidentCaseV2StepForm from '../IncidentCaseV2StepForm'
import type {
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from '../incidentCaseV2Steps'
import StepField from '../StepField'
import {
  DATE_FORMAT,
  StepFieldPair,
  StepSection,
  StepSeparator,
  TIME_FORMAT
} from '../stepLayout'
import {
  type SavedPerson,
  useTeamMemberOptions,
  withSavedPeople
} from '../stepOptions'
import { toInstantIso } from '../stepValues'

const INCIDENT_CATEGORY_ITEM = '5e867702b349f776c8d48c34'
/** The log title's limit, as on the v1 form. */
const TITLE_MAX_LENGTH = 35
const testId = 'incident-case-v2-incident-information'

type IncidentInformationValues = {
  caseOwner?: string
  item?: string | null
  title?: string
  severity?: IncidentSeverity | null
  incidentDate?: Dayjs | null
  incidentTime?: Dayjs | null
  description?: string | null
  location?: string
  goals?: Array<string>
  isNextHandover?: boolean
}

/** The case metadata the read returns; ts-common's report type lacks it. */
type CaseMetadata = {
  caseOwner?: SavedPerson
  caseReference?: string | null
  incidentOccurredAt?: string | null
  incidentEndedAt?: string | null
}

/** The same five levels under the validations package's own enum. */
const toSeverity = (severity: IncidentSeverity | null | undefined) =>
  severity
    ? IncidentReportSeverityEnum[
        IncidentSeverity[severity] as keyof typeof IncidentReportSeverityEnum
      ]
    : null

/**
 * The case owner, from the people who can own a case. On a safeguarding
 * incident that is its safeguarding officers, following the type chosen here.
 */
const CaseOwnerField = ({
  caseOwner,
  placeholder
}: {
  caseOwner: SavedPerson | undefined
  placeholder: string
}) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const form = AntdForm.useFormInstance<IncidentInformationValues>()
  const isSafeguarding =
    AntdForm.useWatch('item', form) === SAFEGUARDING_ITEM_ID
  const teamOwnerOptions = useTeamMemberOptions({
    // Per the design, only senior carers, admin and management own a case.
    filter: isSafeguarding ? canOwnSafeguardingWork : canOwnIncidentWork,
    saved: [caseOwner?.id]
  })
  const officers = useTeamMemberOptions({ filter: canOwnSafeguardingWork })
  // An owner whose account was deleted is named from the read, not picked.
  const caseOwnerOptions = withSavedPeople(
    teamOwnerOptions,
    [caseOwner ?? null],
    { isLocked: true }
  )

  return (
    <StepField
      name="caseOwner"
      isRequired
      dependencies={['item']}
      rules={[
        // Read at validation: a changed type revalidates before it renders.
        ({ getFieldValue }) => ({
          validator: (_, owner?: string) =>
            !owner ||
            getFieldValue('item') !== SAFEGUARDING_ITEM_ID ||
            officers.some((officer) => officer.value === owner)
              ? Promise.resolve()
              : Promise.reject(
                  new Error(
                    t(
                      incidentReportsConstants.CASE_V2_INFO_CASE_OWNER_SAFEGUARDING
                    )
                  )
                )
        })
      ]}
      label={t(incidentReportsConstants.CASE_V2_INFO_CASE_OWNER)}
      hint={t(incidentReportsConstants.CASE_V2_INFO_CASE_OWNER_HINT)}
    >
      <Select
        data-testid={`${testId}-case-owner`}
        options={caseOwnerOptions}
        placeholder={placeholder}
        showSearch
        optionFilterProp="label"
      />
    </StepField>
  )
}

/**
 * Step 1: the case owner, the incident and when it happened, and the log's own
 * fields, which write through to the log. The incident time starts as the
 * logged time.
 */
const IncidentInformationStep = ({
  incidentReport,
  handleRef,
  onDirtyChange
}: IncidentCaseV2StepProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const { goals } = useGoals(incidentReport.serviceUser.id)
  const incidentTypes = useIncidentTypeOptions(INCIDENT_CATEGORY_ITEM)

  const { log } = incidentReport
  const { caseOwner, caseReference, incidentOccurredAt, incidentEndedAt } =
    incidentReport as typeof incidentReport & CaseMetadata
  // Whether the case arrived with an end, fixed on arrival: the refetch after
  // the save that clears it mustn't change what the step sends, or the step
  // would read as changed against that save.
  const [hadStoredEnd] = useState(Boolean(incidentEndedAt))
  const hasLocations = home?.features?.hasHomeLocations?.enabled ?? false

  const initialValues = useMemo<IncidentInformationValues>(
    () => ({
      caseOwner: caseOwner?.id,
      item: log.item?.id,
      title: log.title,
      severity: incidentReport.severity ?? null,
      // Until the case has its own incident time, start from the log's time,
      // which is when the carer app says it happened.
      incidentDate: dayjs(incidentOccurredAt ?? log.timeLogged),
      incidentTime: dayjs(incidentOccurredAt ?? log.timeLogged),
      description: log.description,
      location: log.location,
      goals: log.goals?.map((goal) => goal.id) ?? [],
      isNextHandover: log.isNextHandover ?? false
    }),
    [incidentReport, caseOwner, incidentOccurredAt, log]
  )

  const toStepValues = (
    values: IncidentInformationValues
  ): IncidentCaseV2StepValues => ({
    caseOwner: values.caseOwner,
    severity: toSeverity(values.severity),
    incidentOccurredAt:
      toInstantIso(
        values.incidentDate,
        values.incidentTime,
        incidentOccurredAt ?? log.timeLogged
      ) ?? undefined,
    // The incident is one time now; an end saved by an earlier build is
    // cleared, or a later start would be refused as after it.
    ...(hadStoredEnd ? { incidentEndedAt: null } : {}),
    log: {
      item: values.item ?? null,
      title: values.title ?? '',
      description: values.description ?? null,
      // Read only here, but opening the case requires it, so it goes back as is.
      timeLogged: dayjs(log.timeLogged).toISOString(),
      ...(hasLocations && values.location ? { location: values.location } : {}),
      ...(values.item === SAFEGUARDING_ITEM_ID
        ? {}
        : {
            goals: values.goals ?? [],
            isNextHandover: values.isNextHandover ?? false
          })
    }
  })

  const severityOptions = getSeverityOptions(home?.incidentSeverity, t)
  const locationOptions = home?.locations?.map((location) => ({
    label: location,
    value: location
  }))
  const goalOptions = goals?.map((goal) => ({
    label: goal.title,
    value: goal.id
  }))
  const placeholder = t(incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER)

  return (
    <IncidentCaseV2StepForm
      handleRef={handleRef}
      onDirtyChange={onDirtyChange}
      initialValues={initialValues}
      toStepValues={toStepValues}
      testId={testId}
    >
      <StepSection>
        {caseReference && (
          <Stack gap="50" data-testid={`${testId}-reference`}>
            <Text weight="semibold">
              {t(incidentReportsConstants.CASE_V2_INFO_REFERENCE_ID)}
            </Text>
            <Text tone="secondary">
              {t(incidentReportsConstants.CASE_V2_INFO_REFERENCE_ID_HINT)}
            </Text>
            <Text weight="medium">{caseReference}</Text>
          </Stack>
        )}
        <CaseOwnerField caseOwner={caseOwner} placeholder={placeholder} />
      </StepSection>

      <StepSeparator />

      <StepSection>
        <StepField
          name="item"
          isRequired
          label={t(incidentReportsConstants.CASE_V2_INFO_INCIDENT_TYPE)}
        >
          <Select
            data-testid={`${testId}-incident-type`}
            options={incidentTypes}
            placeholder={placeholder}
          />
        </StepField>
        <StepField
          name="title"
          isRequired
          label={t(incidentReportsConstants.CASE_V2_INFO_TITLE)}
        >
          <Input data-testid={`${testId}-title`} maxLength={TITLE_MAX_LENGTH} />
        </StepField>
        <StepField
          name="severity"
          label={t(incidentReportsConstants.CASE_V2_INFO_INCIDENT_SEVERITY)}
        >
          <Select
            data-testid={`${testId}-severity`}
            options={severityOptions}
            placeholder={placeholder}
          />
        </StepField>
      </StepSection>

      <StepSeparator />

      <StepSection
        title={t(
          incidentReportsConstants.CASE_V2_INFO_INCIDENT_TIME_AND_DETAILS
        )}
      >
        <StepFieldPair>
          <StepField
            name="incidentDate"
            isRequired
            label={t(incidentReportsConstants.CASE_V2_INFO_DATE)}
          >
            <DatePicker
              data-testid={`${testId}-incident-date`}
              format={DATE_FORMAT}
            />
          </StepField>
          <StepField
            name="incidentTime"
            isRequired
            label={t(incidentReportsConstants.CASE_V2_INFO_TIME)}
          >
            <TimePicker
              data-testid={`${testId}-incident-time`}
              format={TIME_FORMAT}
            />
          </StepField>
        </StepFieldPair>
        <StepField
          name="description"
          isRequired
          label={t(incidentReportsConstants.CASE_V2_INFO_INCIDENT_DETAILS)}
          hint={t(incidentReportsConstants.CASE_V2_INFO_INCIDENT_DETAILS_HINT)}
        >
          <Input.TextArea
            data-testid={`${testId}-description`}
            autoSize={{ minRows: 3 }}
          />
        </StepField>
        {hasLocations && (
          <StepField
            name="location"
            label={t(incidentReportsConstants.CASE_V2_INFO_LOCATION)}
          >
            <Select
              data-testid={`${testId}-location`}
              options={locationOptions}
              placeholder={placeholder}
            />
          </StepField>
        )}
      </StepSection>

      <StepSeparator />

      <StepSection title={t(incidentReportsConstants.CASE_V2_INFO_LOGGED_TIME)}>
        {/* When it was logged is the log's own record, so it is shown, not edited. */}
        <StepFieldPair>
          <Stack gap="100" data-testid={`${testId}-logged-date`}>
            <Text weight="semibold">
              {t(incidentReportsConstants.CASE_V2_INFO_DATE)}
            </Text>
            <Text weight="medium">
              {dayjs(log.timeLogged).format(DATE_FORMAT)}
            </Text>
          </Stack>
          <Stack gap="100" data-testid={`${testId}-logged-time`}>
            <Text weight="semibold">
              {t(incidentReportsConstants.CASE_V2_INFO_TIME)}
            </Text>
            <Text weight="medium">
              {dayjs(log.timeLogged).format(TIME_FORMAT)}
            </Text>
          </Stack>
        </StepFieldPair>
        {log.loggedBy?.name && (
          <Stack gap="100" data-testid={`${testId}-logged-by`}>
            <Text weight="semibold">
              {t(incidentReportsConstants.CASE_V2_INFO_LOGGED_BY)}
            </Text>
            <LoggedBy>
              <Avatar name={log.loggedBy.name} size={24} />
              <Text weight="medium">{log.loggedBy.name}</Text>
            </LoggedBy>
          </Stack>
        )}
      </StepSection>

      <GoalsAndHandover goalOptions={goalOptions} />
    </IncidentCaseV2StepForm>
  )
}

/** Not for a safeguarding log; follows the type as it is picked. */
const GoalsAndHandover = ({
  goalOptions
}: {
  goalOptions?: Array<{ label: string; value: string }>
}) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const form = AntdForm.useFormInstance<IncidentInformationValues>()
  const item = AntdForm.useWatch('item', form)

  if (item === SAFEGUARDING_ITEM_ID) {
    return null
  }

  return (
    <>
      <StepSeparator />

      <StepSection>
        <StepField
          name="goals"
          label={t(incidentReportsConstants.CASE_V2_INFO_LINKED_GOALS)}
        >
          <Select
            data-testid={`${testId}-goals`}
            mode="multiple"
            allowClear
            options={goalOptions}
            placeholder={t(
              incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER_MULTI
            )}
          />
        </StepField>
        <StepField
          name="isNextHandover"
          valuePropName="checked"
          label={t(incidentReportsConstants.CASE_V2_INFO_HANDOVER)}
        >
          <Checkbox data-testid={`${testId}-handover`}>
            {t(incidentReportsConstants.CASE_V2_INFO_FLAG_FOR_HANDOVER)}
          </Checkbox>
        </StepField>
      </StepSection>
    </>
  )
}

const LoggedBy = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssVars.spacing[100]};
`

export default IncidentInformationStep
